import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Calendar,
  Clock,
  Users,
  Send,
  Plus,
  Edit2,
  Trash2,
  Play,
  Pause,
  BarChart3,
  Sparkles,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  MessageCircle,
  Eye,
  Reply,
  Target,
  Zap,
  CalendarDays,
  RefreshCw,
  FlaskConical,
  Trophy,
  Percent,
  RotateCcw
} from 'lucide-react';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const SCHEDULE_TYPES = {
  daily: { label: 'Daily', icon: CalendarDays },
  weekly: { label: 'Weekly', icon: Calendar },
  monthly: { label: 'Monthly', icon: CalendarDays },
  custom: { label: 'Custom Interval', icon: Clock }
};

const WINNING_METRICS = {
  engagement: 'Engagement Score (Open + Response)',
  open_rate: 'Open Rate',
  response_rate: 'Response Rate'
};

export default function CampaignsPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState({});
  const [targetingOptions, setTargetingOptions] = useState({});
  const [teams, setTeams] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [abResultsData, setAbResultsData] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_id: '',
    schedule_type: 'weekly',
    day_of_week: 0,
    day_of_month: 1,
    hour: 9,
    minute: 0,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    interval_days: 7,
    targeting: 'all_clients',
    team_ids: [],
    use_ai: true,
    ai_prompt: '',
    custom_message: '',
    enabled: true,
    // A/B Testing
    ab_testing_enabled: false,
    variants: [
      { name: 'Variant A', ai_prompt: '', custom_message: '', split_percentage: 50 },
      { name: 'Variant B', ai_prompt: '', custom_message: '', split_percentage: 50 }
    ],
    min_sends_for_winner: 50,
    winning_metric: 'engagement'
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      const [campaignsRes, templatesRes, targetingRes] = await Promise.all([
        axios.get(`${API}/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/campaigns/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/campaigns/targeting-options`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setCampaigns(campaignsRes.data.campaigns || []);
      setTemplates(templatesRes.data || {});
      setTargetingOptions(targetingRes.data || {});
      
      // Fetch teams separately (optional)
      try {
        const teamsRes = await axios.get(`${API}/teams`, { headers: { Authorization: `Bearer ${token}` } });
        setTeams(teamsRes.data.teams || []);
      } catch (e) {
        console.log('Teams not available');
        setTeams([]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchCampaigns();
    }
  }, [token, fetchCampaigns]);

  const handleTemplateSelect = (templateId) => {
    const template = templates[templateId];
    if (template) {
      setFormData(prev => ({
        ...prev,
        template_id: templateId,
        name: template.name,
        description: template.description,
        ai_prompt: template.ai_prompt,
        schedule_type: template.default_schedule?.type || 'weekly',
        day_of_week: template.default_schedule?.day_of_week ?? 0,
        day_of_month: template.default_schedule?.day_of_month ?? 1,
        hour: template.default_schedule?.hour ?? 9,
        minute: template.default_schedule?.minute ?? 0
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCampaign) {
        await axios.put(`${API}/campaigns/${editingCampaign.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Campaign updated!');
      } else {
        await axios.post(`${API}/campaigns`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Campaign created!');
      }
      
      setShowCreateModal(false);
      setEditingCampaign(null);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      template_id: '',
      schedule_type: 'weekly',
      day_of_week: 0,
      day_of_month: 1,
      hour: 9,
      minute: 0,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      interval_days: 7,
      targeting: 'all_clients',
      team_ids: [],
      use_ai: true,
      ai_prompt: '',
      custom_message: '',
      enabled: true,
      ab_testing_enabled: false,
      variants: [
        { name: 'Variant A', ai_prompt: '', custom_message: '', split_percentage: 50 },
        { name: 'Variant B', ai_prompt: '', custom_message: '', split_percentage: 50 }
      ],
      min_sends_for_winner: 50,
      winning_metric: 'engagement'
    });
  };

  const viewAbResults = async (campaign) => {
    setSelectedCampaign(campaign);
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.id}/ab-results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAbResultsData(response.data);
      setAnalyticsData(null);
    } catch (error) {
      toast.error('Failed to load A/B results');
    }
  };

  const determineWinner = async (campaignId) => {
    try {
      const response = await axios.post(`${API}/campaigns/${campaignId}/determine-winner`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchCampaigns();
      if (selectedCampaign?.id === campaignId) {
        viewAbResults(selectedCampaign);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to determine winner');
    }
  };

  const resetAbTest = async (campaignId) => {
    if (!confirm('Reset A/B test? This will clear all variant stats.')) return;
    try {
      await axios.post(`${API}/campaigns/${campaignId}/reset-ab-test`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('A/B test reset');
      fetchCampaigns();
      if (selectedCampaign?.id === campaignId) {
        viewAbResults(selectedCampaign);
      }
    } catch (error) {
      toast.error('Failed to reset A/B test');
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description || '',
      template_id: campaign.template_id || '',
      schedule_type: campaign.schedule?.type || 'weekly',
      day_of_week: campaign.schedule?.day_of_week ?? 0,
      day_of_month: campaign.schedule?.day_of_month ?? 1,
      hour: campaign.schedule?.hour ?? 9,
      minute: campaign.schedule?.minute ?? 0,
      timezone: campaign.schedule?.timezone || 'UTC',
      interval_days: campaign.schedule?.interval_days ?? 7,
      targeting: campaign.targeting || 'all_clients',
      team_ids: campaign.team_ids || [],
      use_ai: campaign.message_config?.use_ai ?? true,
      ai_prompt: campaign.message_config?.ai_prompt || '',
      custom_message: campaign.message_config?.custom_message || '',
      enabled: campaign.enabled
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (campaignId) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleToggle = async (campaignId) => {
    try {
      const response = await axios.post(`${API}/campaigns/${campaignId}/toggle`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to toggle campaign');
    }
  };

  const handleSendNow = async (campaignId) => {
    try {
      const response = await axios.post(`${API}/campaigns/${campaignId}/send-now`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to send campaign');
    }
  };

  const viewAnalytics = async (campaign) => {
    setSelectedCampaign(campaign);
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.id}/analytics?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyticsData(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    }
  };

  const formatNextSend = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="campaigns-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl uppercase">
            <span className="text-white">MESSAGE</span>{' '}
            <span className="text-primary">CAMPAIGNS</span>
          </h1>
          <p className="text-gray-400 mt-1">Schedule recurring messages to engage your clients</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="bg-primary hover:bg-red-700"
          data-testid="create-campaign-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Campaign
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card-tactical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-sm">
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-mono text-white">{campaigns.length}</div>
              <div className="text-xs text-gray-500">Total Campaigns</div>
            </div>
          </div>
        </div>
        <div className="card-tactical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-sm">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <div className="text-2xl font-mono text-white">
                {campaigns.filter(c => c.enabled).length}
              </div>
              <div className="text-xs text-gray-500">Active</div>
            </div>
          </div>
        </div>
        <div className="card-tactical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-sm">
              <Send className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-mono text-white">
                {campaigns.reduce((sum, c) => sum + (c.stats?.total_sent || 0), 0)}
              </div>
              <div className="text-xs text-gray-500">Messages Sent</div>
            </div>
          </div>
        </div>
        <div className="card-tactical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-sm">
              <Eye className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <div className="text-2xl font-mono text-white">
                {campaigns.reduce((sum, c) => sum + (c.stats?.total_opened || 0), 0)}
              </div>
              <div className="text-xs text-gray-500">Total Opens</div>
            </div>
          </div>
        </div>
      </div>

      {/* Template Quick Start */}
      <div className="card-tactical">
        <h3 className="font-heading text-lg text-white uppercase mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          Quick Start Templates
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(templates).map(([id, template]) => (
            <button
              key={id}
              onClick={() => { handleTemplateSelect(id); setShowCreateModal(true); }}
              className="p-4 bg-secondary/50 rounded-sm border border-border hover:border-primary transition-colors text-left"
              data-testid={`template-${id}`}
            >
              <div className="font-medium text-white">{template.name}</div>
              <div className="text-xs text-gray-400 mt-1">{template.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Campaigns List */}
      <div className="space-y-3">
        <h3 className="font-heading text-lg text-white uppercase">Your Campaigns</h3>
        
        {campaigns.length === 0 ? (
          <div className="card-tactical text-center py-12">
            <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No campaigns yet</p>
            <p className="text-sm text-gray-500 mt-1">Create your first campaign to start engaging clients</p>
          </div>
        ) : (
          campaigns.map(campaign => (
            <div
              key={campaign.id}
              data-testid={`campaign-${campaign.id}`}
              className={`card-tactical p-4 border-l-4 ${
                campaign.enabled ? 'border-l-green-500' : 'border-l-gray-600'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h4 className="font-medium text-white">{campaign.name}</h4>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      campaign.enabled 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {campaign.enabled ? 'Active' : 'Paused'}
                    </span>
                    {campaign.message_config?.use_ai && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        AI
                      </span>
                    )}
                    {campaign.ab_testing?.enabled && (
                      <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                        campaign.ab_testing?.winner_determined
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        <FlaskConical className="w-3 h-3" />
                        {campaign.ab_testing?.winner_determined ? 'Winner Found' : 'A/B Test'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1">{campaign.description}</p>
                  
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {SCHEDULE_TYPES[campaign.schedule?.type]?.label || 'Weekly'}
                      {campaign.schedule?.type === 'weekly' && ` - ${DAYS_OF_WEEK[campaign.schedule.day_of_week]}`}
                      {` at ${campaign.schedule?.hour || 9}:${String(campaign.schedule?.minute || 0).padStart(2, '0')}`}
                    </span>
                    <span className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      {targetingOptions[campaign.targeting] || campaign.targeting}
                    </span>
                    <span className="flex items-center gap-1">
                      <Send className="w-3 h-3" />
                      {campaign.stats?.total_sent || 0} sent
                    </span>
                  </div>
                  
                  <div className="mt-2 text-xs">
                    <span className="text-gray-500">Next send: </span>
                    <span className="text-primary">{formatNextSend(campaign.stats?.next_send_at)}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {campaign.ab_testing?.enabled && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => viewAbResults(campaign)}
                      className="text-blue-400 hover:text-blue-300"
                      title="A/B Results"
                    >
                      <FlaskConical className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => viewAnalytics(campaign)}
                    className="text-gray-400 hover:text-white"
                    title="View Analytics"
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSendNow(campaign.id)}
                    className="text-blue-400 hover:text-blue-300"
                    title="Send Now"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggle(campaign.id)}
                    className={campaign.enabled ? 'text-yellow-400 hover:text-yellow-300' : 'text-green-400 hover:text-green-300'}
                    title={campaign.enabled ? 'Pause' : 'Resume'}
                  >
                    {campaign.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(campaign)}
                    className="text-gray-400 hover:text-white"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(campaign.id)}
                    className="text-red-400 hover:text-red-300"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="font-heading text-2xl text-white uppercase mb-6">
                {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Campaign Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                      required
                      data-testid="campaign-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0 h-20"
                    />
                  </div>
                </div>

                {/* Schedule */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-white uppercase">Schedule</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Frequency</label>
                      <select
                        value={formData.schedule_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, schedule_type: e.target.value }))}
                        className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                        data-testid="schedule-type-select"
                      >
                        {Object.entries(SCHEDULE_TYPES).map(([key, { label }]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
                    
                    {formData.schedule_type === 'weekly' && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Day of Week</label>
                        <select
                          value={formData.day_of_week}
                          onChange={(e) => setFormData(prev => ({ ...prev, day_of_week: parseInt(e.target.value) }))}
                          className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                        >
                          {DAYS_OF_WEEK.map((day, idx) => (
                            <option key={idx} value={idx}>{day}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {formData.schedule_type === 'monthly' && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Day of Month</label>
                        <select
                          value={formData.day_of_month}
                          onChange={(e) => setFormData(prev => ({ ...prev, day_of_month: parseInt(e.target.value) }))}
                          className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                        >
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                            <option key={day} value={day}>{day}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {formData.schedule_type === 'custom' && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Every X Days</label>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={formData.interval_days}
                          onChange={(e) => setFormData(prev => ({ ...prev, interval_days: parseInt(e.target.value) }))}
                          className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Hour</label>
                      <select
                        value={formData.hour}
                        onChange={(e) => setFormData(prev => ({ ...prev, hour: parseInt(e.target.value) }))}
                        className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Minute</label>
                      <select
                        value={formData.minute}
                        onChange={(e) => setFormData(prev => ({ ...prev, minute: parseInt(e.target.value) }))}
                        className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                      >
                        {[0, 15, 30, 45].map(min => (
                          <option key={min} value={min}>{String(min).padStart(2, '0')}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Timezone</label>
                      <input
                        type="text"
                        value={formData.timezone}
                        onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                        className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                        placeholder="UTC"
                      />
                    </div>
                  </div>
                </div>

                {/* Targeting */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-white uppercase">Targeting</h4>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Send To</label>
                    <select
                      value={formData.targeting}
                      onChange={(e) => setFormData(prev => ({ ...prev, targeting: e.target.value }))}
                      className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                      data-testid="targeting-select"
                    >
                      {Object.entries(targetingOptions).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                  
                  {formData.targeting === 'team' && teams.length > 0 && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Select Teams</label>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {teams.map(team => (
                          <label key={team.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.team_ids.includes(team.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData(prev => ({ ...prev, team_ids: [...prev.team_ids, team.id] }));
                                } else {
                                  setFormData(prev => ({ ...prev, team_ids: prev.team_ids.filter(id => id !== team.id) }));
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-600 bg-secondary text-primary"
                            />
                            <span className="text-sm text-white">{team.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Message */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-white uppercase">Message</h4>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.use_ai}
                      onChange={(e) => setFormData(prev => ({ ...prev, use_ai: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-600 bg-secondary text-primary"
                    />
                    <span className="text-sm text-white flex items-center gap-1">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      Use AI to personalize messages
                    </span>
                  </label>
                  
                  {formData.use_ai ? (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">AI Prompt</label>
                      <textarea
                        value={formData.ai_prompt}
                        onChange={(e) => setFormData(prev => ({ ...prev, ai_prompt: e.target.value }))}
                        className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0 h-24"
                        placeholder="Describe what kind of message the AI should generate..."
                        data-testid="ai-prompt-input"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Message Template</label>
                      <textarea
                        value={formData.custom_message}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_message: e.target.value }))}
                        className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0 h-24"
                        placeholder="Use {name} to insert client's name..."
                      />
                    </div>
                  )}
                </div>

                {/* A/B Testing */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-white uppercase flex items-center gap-2">
                      <FlaskConical className="w-4 h-4 text-blue-400" />
                      A/B Testing
                    </h4>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.ab_testing_enabled}
                        onChange={(e) => setFormData(prev => ({ ...prev, ab_testing_enabled: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-600 bg-secondary text-primary"
                      />
                      <span className="text-xs text-gray-400">
                        {formData.ab_testing_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>
                  
                  {formData.ab_testing_enabled && (
                    <div className="space-y-4 p-4 bg-blue-500/5 border border-blue-500/20 rounded-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Min Sends for Winner</label>
                          <input
                            type="number"
                            min="10"
                            max="500"
                            value={formData.min_sends_for_winner}
                            onChange={(e) => setFormData(prev => ({ ...prev, min_sends_for_winner: parseInt(e.target.value) }))}
                            className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Winning Metric</label>
                          <select
                            value={formData.winning_metric}
                            onChange={(e) => setFormData(prev => ({ ...prev, winning_metric: e.target.value }))}
                            className="w-full bg-secondary text-white rounded-sm px-3 py-2 border-0"
                          >
                            {Object.entries(WINNING_METRICS).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">Variants</span>
                          {formData.variants.length < 4 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                variants: [...prev.variants, {
                                  name: `Variant ${String.fromCharCode(65 + prev.variants.length)}`,
                                  ai_prompt: '',
                                  custom_message: '',
                                  split_percentage: Math.floor(100 / (prev.variants.length + 1))
                                }]
                              }))}
                              className="text-blue-400"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Variant
                            </Button>
                          )}
                        </div>
                        
                        {formData.variants.map((variant, idx) => (
                          <div key={idx} className="p-3 bg-secondary/50 rounded-sm space-y-2">
                            <div className="flex items-center justify-between">
                              <input
                                type="text"
                                value={variant.name}
                                onChange={(e) => {
                                  const newVariants = [...formData.variants];
                                  newVariants[idx].name = e.target.value;
                                  setFormData(prev => ({ ...prev, variants: newVariants }));
                                }}
                                className="bg-transparent text-white font-medium border-0 p-0 text-sm"
                                placeholder={`Variant ${String.fromCharCode(65 + idx)}`}
                              />
                              <div className="flex items-center gap-2">
                                <Percent className="w-3 h-3 text-gray-500" />
                                <input
                                  type="number"
                                  min="1"
                                  max="99"
                                  value={variant.split_percentage}
                                  onChange={(e) => {
                                    const newVariants = [...formData.variants];
                                    newVariants[idx].split_percentage = parseInt(e.target.value) || 0;
                                    setFormData(prev => ({ ...prev, variants: newVariants }));
                                  }}
                                  className="w-16 bg-secondary text-white rounded-sm px-2 py-1 text-xs border-0 text-right"
                                />
                                {formData.variants.length > 2 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setFormData(prev => ({
                                        ...prev,
                                        variants: prev.variants.filter((_, i) => i !== idx)
                                      }));
                                    }}
                                    className="text-red-400 p-1 h-auto"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <textarea
                              value={formData.use_ai ? variant.ai_prompt : variant.custom_message}
                              onChange={(e) => {
                                const newVariants = [...formData.variants];
                                if (formData.use_ai) {
                                  newVariants[idx].ai_prompt = e.target.value;
                                } else {
                                  newVariants[idx].custom_message = e.target.value;
                                }
                                setFormData(prev => ({ ...prev, variants: newVariants }));
                              }}
                              className="w-full bg-secondary text-white rounded-sm px-2 py-1 text-xs border-0 h-16"
                              placeholder={formData.use_ai ? "AI prompt for this variant..." : "Message for this variant..."}
                            />
                          </div>
                        ))}
                        
                        {formData.variants.reduce((sum, v) => sum + v.split_percentage, 0) !== 100 && (
                          <p className="text-xs text-red-400">
                            Split percentages must total 100% (currently {formData.variants.reduce((sum, v) => sum + v.split_percentage, 0)}%)
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-600 bg-secondary text-primary"
                    />
                    <span className="text-sm text-white">Enable campaign immediately</span>
                  </label>
                  
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setShowCreateModal(false); setEditingCampaign(null); resetForm(); }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-red-700" data-testid="save-campaign-btn">
                      {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {selectedCampaign && analyticsData && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-sm max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl text-white uppercase">
                  {analyticsData.campaign_name} Analytics
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => { setSelectedCampaign(null); setAnalyticsData(null); }}
                >
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-secondary/50 p-4 rounded-sm text-center">
                  <div className="text-2xl font-mono text-white">{analyticsData.summary.total_sends}</div>
                  <div className="text-xs text-gray-500">Total Sends</div>
                </div>
                <div className="bg-secondary/50 p-4 rounded-sm text-center">
                  <div className="text-2xl font-mono text-white">{analyticsData.summary.total_recipients}</div>
                  <div className="text-xs text-gray-500">Recipients</div>
                </div>
                <div className="bg-green-500/10 p-4 rounded-sm text-center">
                  <div className="text-2xl font-mono text-green-400">{analyticsData.summary.open_rate}%</div>
                  <div className="text-xs text-gray-500">Open Rate</div>
                </div>
                <div className="bg-blue-500/10 p-4 rounded-sm text-center">
                  <div className="text-2xl font-mono text-blue-400">{analyticsData.summary.response_rate}%</div>
                  <div className="text-xs text-gray-500">Response Rate</div>
                </div>
              </div>
              
              {/* Recent Sends */}
              {analyticsData.recent_sends?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-white uppercase mb-3">Recent Sends</h4>
                  <div className="space-y-2">
                    {analyticsData.recent_sends.map(send => (
                      <div key={send.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                        <div>
                          <div className="text-sm text-white">
                            {new Date(send.sent_at).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </div>
                          <div className="text-xs text-gray-500">{send.recipients_count} recipients</div>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-yellow-400">
                            <Eye className="w-3 h-3" />
                            {send.opened_count}
                          </span>
                          <span className="flex items-center gap-1 text-blue-400">
                            <Reply className="w-3 h-3" />
                            {send.responded_count}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* A/B Results Modal */}
      {selectedCampaign && abResultsData && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-sm max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl text-white uppercase flex items-center gap-2">
                  <FlaskConical className="w-6 h-6 text-blue-400" />
                  A/B Test Results
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => { setSelectedCampaign(null); setAbResultsData(null); }}
                >
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Status Banner */}
              <div className={`p-4 rounded-sm mb-6 ${
                abResultsData.winner_determined 
                  ? 'bg-yellow-500/10 border border-yellow-500/30'
                  : abResultsData.can_determine_winner
                    ? 'bg-green-500/10 border border-green-500/30'
                    : 'bg-blue-500/10 border border-blue-500/30'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    {abResultsData.winner_determined ? (
                      <p className="text-yellow-400 font-medium flex items-center gap-2">
                        <Trophy className="w-5 h-5" />
                        Winner found and auto-switched!
                      </p>
                    ) : abResultsData.can_determine_winner ? (
                      <p className="text-green-400 font-medium">
                        Enough data collected. Ready to determine winner!
                      </p>
                    ) : (
                      <p className="text-blue-400">
                        Collecting data... {abResultsData.total_sent} / {abResultsData.min_sends_for_winner} sends
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Winning metric: {WINNING_METRICS[abResultsData.winning_metric]}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {abResultsData.winner_determined ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resetAbTest(selectedCampaign.id)}
                        className="border-yellow-500/30 text-yellow-400"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Reset Test
                      </Button>
                    ) : abResultsData.can_determine_winner && (
                      <Button
                        size="sm"
                        onClick={() => determineWinner(selectedCampaign.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Trophy className="w-3 h-3 mr-1" />
                        Determine Winner
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Variants Comparison */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-white uppercase">Variants Performance</h4>
                
                {abResultsData.variants?.map((variant, idx) => (
                  <div
                    key={variant.id}
                    className={`p-4 rounded-sm border ${
                      variant.is_winner
                        ? 'bg-yellow-500/10 border-yellow-500/30'
                        : 'bg-secondary/30 border-border'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{variant.name}</span>
                        {variant.is_winner && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400 flex items-center gap-1">
                            <Trophy className="w-3 h-3" />
                            Winner
                          </span>
                        )}
                        {variant.is_control && !variant.is_winner && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-500/20 text-gray-400">
                            Control
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {variant.split_percentage}% split
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-xl font-mono text-white">{variant.stats?.sent || 0}</div>
                        <div className="text-xs text-gray-500">Sent</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-mono text-yellow-400">{variant.stats?.open_rate || 0}%</div>
                        <div className="text-xs text-gray-500">Open Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-mono text-blue-400">{variant.stats?.response_rate || 0}%</div>
                        <div className="text-xs text-gray-500">Response Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-mono text-green-400">{variant.stats?.engagement_score || 0}</div>
                        <div className="text-xs text-gray-500">Engagement</div>
                      </div>
                    </div>
                    
                    {/* Visual bar comparison */}
                    <div className="mt-3 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-16">Open</span>
                        <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-yellow-500"
                            style={{ width: `${Math.min(variant.stats?.open_rate || 0, 100)}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-16">Response</span>
                        <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500"
                            style={{ width: `${Math.min(variant.stats?.response_rate || 0, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
